import { useContext, useState } from 'react';

import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/videa-components/src/index';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import type { TrialStatus } from '../types';

import { TrialSuccessAlert } from './TrialSuccessAlert';

interface Props {
  isCentralizedBilling?: boolean;
  trialLengthInDays?: number;
  onMoreInfo?: () => void;
  trialStatus?: TrialStatus;
  onStartTrial: () => Promise<void>;
  isTrialLoading: boolean;
  isTrialError: boolean;
  isTrialSuccess: boolean;
  onOpenOnboarding?: () => void;
}

export const TrialOfferCollapsible = ({
  isCentralizedBilling,
  trialLengthInDays,
  onMoreInfo,
  trialStatus,
  onStartTrial,
  isTrialLoading,
  isTrialError,
  isTrialSuccess,
  onOpenOnboarding,
}: Props) => {
  const [isOpen, setIsOpen] = useState(true);

  const externalPatientContext = useContext(ExternalPatientContext);
  const { trackEvent } = useTrackEvent();
  const trialDurationDays = trialLengthInDays ?? 30;

  const handleStartTrial = async () => {
    trackEvent(mixpanelEvents.externalTrialOfferClick, {
      ...externalPatientContext,
      trialStatus: trialStatus ?? null,
      centralizedBilling: isCentralizedBilling ?? null,
      isIframe: true,
    });
    await onStartTrial();
  };

  return (
    <Collapsible
      className="vai-px-2 vai-bg-zinc-700 vai-text-white vai-p-3 vai-rounded-md vai-flex vai-flex-col data-[state=open]:vai-gap-2"
      onOpenChange={(open) => {
        setIsOpen(open);
        trackEvent(mixpanelEvents.externalCollapsibleToggle, {
          ...externalPatientContext,
          trialStatus: trialStatus ?? null,
          centralizedBilling: isCentralizedBilling ?? null,
          isIframe: true,
          isOpen: open,
          source: 'TRIAL_OFFER',
        });
      }}
      open={isOpen}
    >
      <CollapsibleTrigger className="vai-w-full vai-flex vai-items-center vai-justify-between">
        <p className="vai-font-sans vai-font-semibold vai-text-lg vai-leading-6">
          <Trans>Your imaging now has AI built in!</Trans>
        </p>
        <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} />
      </CollapsibleTrigger>

      <CollapsibleContent className="vai-flex vai-flex-col vai-gap-2">
        <img
          alt="AI findings preview"
          className="vai-rounded-lg vai-w-full vai-object-contain"
          src="assets/images/external/image-with-findings.png"
        />
        <p className="vai-font-inter vai-text-sm">
          <Trans>
            See AI findings on all your x-rays - no commitment necessary.*
          </Trans>
        </p>

        {isTrialSuccess ? (
          <TrialSuccessAlert onOpenOnboarding={onOpenOnboarding} />
        ) : (
          <>
            <Button
              className="vai-w-full"
              disabled={isTrialLoading}
              onClick={handleStartTrial}
            >
              {isTrialLoading && (
                <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-2 vai-border-t-transparent" />
              )}
              <Trans>Try Detect AI</Trans>
            </Button>
            {onMoreInfo && (
              <Button
                className="vai-w-full !vai-bg-[#27272A] !vai-text-zinc-50 hover:!vai-bg-[#27272A]/90"
                onClick={onMoreInfo}
                variant="secondary"
              >
                <Trans>Learn More</Trans>
              </Button>
            )}
            {isTrialError && (
              <Alert variant="destructiveSolid">
                <FontAwesomeIcon icon={faCircleInfo} />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  <Trans>
                    Something went wrong when starting your trial. Please try
                    again.
                  </Trans>
                </AlertDescription>
              </Alert>
            )}
            <p className="vai-font-inter vai-text-[10px] vai-text-slate-400">
              <Trans>
                *By accepting this evaluation product, you agree to these{' '}
                <a
                  className="vai-text-blue-500 vai-underline"
                  href="https://hsps.widen.net/s/rj8hgnltws/eservices---hs1-terms-and-conditions"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    trackEvent(mixpanelEvents.externalTermsAndConditionsClick, {
                      ...externalPatientContext,
                      source: 'TRIAL_OFFER',
                    });
                  }}
                >
                  terms and conditions
                </a>{' '}
                and that you will not charge, or be reimbursed by any federal
                program (e.g., Medicare, Medicaid, Tricare of Children&apos;s
                Health Insurance Program), or any third party for such
                evaluation product or its use. The evaluation trial period will
                last for {trialDurationDays} days and will automatically cease
                at the end of such period.
              </Trans>
            </p>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
